import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Api
import api from '../api/Base';

// Types
import Action from '../types/Action';
import AlertRules from '../types/Alerts/AlertRules';
import InstanceTarget from '../types/instance/InstanceTarget';
import HostTarget from '../types/host/HostTarget';
import {
    AlertRuleFormData,
    AlertRuleFormState,
    AlertRuleFormValidation,
    createDefaultFormData,
    createDefaultValidation,
    createDefaultUIState,
    createDefaultDependencies
} from '../types/Alerts/AlertRuleFormState';

// Utils
import { AlertRuleFormValidator, AlertRuleFormUtils } from '../helpers/AlertRuleFormValidation';

// Constants
import { ACTIONS, DATA_LOADED } from '../component/Constants';

interface UseAlertRuleFormProps {
    alertRules: AlertRules[];
    instances: InstanceTarget[];
    hosts: HostTarget[];
    loading: number;
    refresh: Function;
}

export const useAlertRuleForm = ({ 
    alertRules, 
    instances, 
    hosts, 
    loading, 
    refresh 
}: UseAlertRuleFormProps) => {
    const navigate = useNavigate();
    const { actionId, alertRuleId } = useParams();

    // Main state
    const [formState, setFormState] = useState<AlertRuleFormState>({
        formData: createDefaultFormData(),
        validation: createDefaultValidation(),
        uiState: createDefaultUIState(actionId === ACTIONS.DELETE.type),
        dependencies: createDefaultDependencies()
    });

    const [action, setAction] = useState<Action>(ACTIONS.CREATE);

    // Update form data
    const updateFormData = useCallback((updates: Partial<AlertRuleFormData>) => {
        setFormState(prev => ({
            ...prev,
            formData: { ...prev.formData, ...updates }
        }));
    }, []);

    // Update validation
    const updateValidation = useCallback((updates: Partial<AlertRuleFormValidation>) => {
        setFormState(prev => ({
            ...prev,
            validation: { ...prev.validation, ...updates }
        }));
    }, []);

    // Update UI state
    const updateUIState = useCallback((updates: Partial<typeof formState.uiState>) => {
        setFormState(prev => ({
            ...prev,
            uiState: { ...prev.uiState, ...updates }
        }));
    }, []);

    // Load alert rule data
    const loadAlertRule = useCallback((alertRuleId: string) => {
        const rule = alertRules.find(r => parseInt(r.id) === Number(alertRuleId));
        
        if (!rule) {
            updateUIState({ isMissing: true });
            return;
        }

        updateFormData({
            id: rule.id,
            name: rule.name,
            disabled: rule.disabled,
            threshold: rule.threshold,
            decreasing: rule.decreasing ? 'true' : 'false',
            entitytype: rule.entitytype,
            metrictype: rule.metrictype,
            aggregation: rule.aggregation,
            statistic: rule.statistic,
            hostid: rule.hostid,
            datasourceid: rule.datasourceid,
            units: rule.units
        });
    }, [alertRules, updateFormData, updateUIState]);

    // Set action based on actionId
    const setActionFromId = useCallback(() => {
        switch (actionId) {
            case 'create':
                setAction(ACTIONS.CREATE);
                break;
            case 'edit':
                setAction(ACTIONS.UPDATE);
                break;
            case 'delete':
                setAction(ACTIONS.DELETE);
                updateUIState({ disabled: true });
                break;
            default:
                console.error('Unknown action requested', actionId);
                updateUIState({ isError: true });
                break;
        }
    }, [actionId, updateUIState]);

    // Validation handlers
    const validateName = useCallback(() => {
        const validation = AlertRuleFormValidator.validateName(
            formState.formData.name,
            formState.formData.id,
            alertRules
        );
        
        updateValidation({ name: validation });
        updateFormData({ name: formState.formData.name.trim() });
    }, [formState.formData.name, formState.formData.id, alertRules, updateValidation, updateFormData]);

    const validateThreshold = useCallback(() => {
        const validation = AlertRuleFormValidator.validateThreshold(
            formState.formData.threshold,
            formState.formData.units
        );
        updateValidation({ threshold: validation });
    }, [formState.formData.threshold, formState.formData.units, updateValidation]);

    const validateStatistic = useCallback(() => {
        const validation = AlertRuleFormValidator.validateStatistic(formState.formData.statistic);
        updateValidation({ statistic: validation });
        return validation.isValid;
    }, [formState.formData.statistic, updateValidation]);

    // Reset fields when type changes
    const resetFieldsForType = useCallback((typeValue: string) => {
        const entityType = AlertRuleFormUtils.getEntityOrMetric('entity', typeValue);
        const metricType = AlertRuleFormUtils.getEntityOrMetric('metric', typeValue);
        
        updateFormData({
            entitytype: entityType,
            metrictype: metricType,
            hostid: '',
            datasourceid: '',
            statistic: ''
        });
    }, [updateFormData]);

    // Submit handler
    const handleSubmit = useCallback(async (event?: React.FormEvent) => {
        if (event) {
            event.preventDefault();
        }

        updateUIState({ loading: true });

        // Validate statistic first
        const isStatisticValid = validateStatistic();
        
        // Validate the complete form
        const { isValid, validation } = AlertRuleFormValidator.validateCompleteForm(
            formState.formData,
            alertRules
        );

        updateValidation(validation);

        if (!isValid || !isStatisticValid) {
            updateUIState({ loading: false });
            return;
        }

        try {
            const serverData = [{
                id: formState.formData.id,
                hostid: formState.formData.hostid,
                datasourceid: formState.formData.datasourceid,
                name: formState.formData.name,
                entitytype: formState.formData.entitytype,
                metrictype: formState.formData.metrictype,
                intervalminutes: formState.formData.intervalminutes,
                threshold: formState.formData.threshold,
                units: formState.formData.units,
                decreasing: formState.formData.decreasing === 'true',
                disabled: formState.formData.disabled,
                statistic: formState.formData.statistic,
                aggregation: formState.formData.aggregation
            }];

            let result;
            const url = '/alert/rule';

            switch (action.type) {
                case 'create':
                    result = await api.post(url, serverData);
                    break;
                case 'update':
                    result = await api.put(url, serverData);
                    break;
                case 'delete':
                    result = await api.delete(url, { data: serverData });
                    break;
                default:
                    console.error('Unknown action requested', action);
                    updateUIState({ isError: true, loading: false });
                    return;
            }

            if (result && result.status === 200) {
                navigate(-1);
                refresh();
            } else {
                updateUIState({ isError: true, loading: false });
            }

        } catch (error) {
            console.error('Failed to complete requested action', action, error);
            updateUIState({ isError: true, loading: false });
        }
    }, [formState, alertRules, action, navigate, refresh, updateUIState, updateValidation, validateStatistic]);

    // Initialize form
    useEffect(() => {
        setActionFromId();
        
        if (alertRuleId && alertRules.length > 0) {
            loadAlertRule(alertRuleId);
        }
    }, [alertRuleId, alertRules, actionId]);

    // Check for missing rule
    useEffect(() => {
        if (loading === DATA_LOADED && !formState.formData.id && action.type === ACTIONS.UPDATE.type) {
            updateUIState({ isMissing: true });
        }
    }, [loading, action.type, formState.formData.id, updateUIState]);

    return {
        formState,
        action,
        updateFormData,
        updateValidation,
        updateUIState,
        validateName,
        validateThreshold,
        validateStatistic,
        handleSubmit,
        resetFieldsForType,
        // Sorted instances and hosts
        sortedInstances: instances.sort((a, b) => a.name.localeCompare(b.name)),
        sortedHosts: hosts.sort((a, b) => a.name.localeCompare(b.name))
    };
};

